/**
 * Seeded Random Number Generator using Mulberry32 algorithm
 * Used for deterministic combat synchronization in multiplayer
 */
export class SeededRNG {
  private state: number;
  private initialSeed: number;

  constructor(seed: number) {
    this.initialSeed = seed;
    this.state = seed;
  }

  /**
   * Generate next random number in [0, 1)
   * Uses mulberry32 algorithm - fast, good distribution
   */
  random(): number {
    let t = (this.state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /**
   * Generate random integer in range [min, max] inclusive
   */
  randomInt(min: number, max: number): number {
    return Math.floor(this.random() * (max - min + 1)) + min;
  }

  /**
   * Generate random boolean with given probability of true
   */
  randomBool(probability: number = 0.5): boolean {
    return this.random() < probability;
  }

  /**
   * Pick random element from array
   */
  pick<T>(array: T[]): T | undefined {
    if (array.length === 0) return undefined;
    return array[Math.floor(this.random() * array.length)];
  }

  /**
   * Shuffle array in place using Fisher-Yates
   */
  shuffle<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(this.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  /**
   * Reset RNG to initial seed
   */
  reset(): void {
    this.state = this.initialSeed;
  }

  /**
   * Get current seed for serialization
   */
  getSeed(): number {
    return this.initialSeed;
  }

  /**
   * Get current state for mid-combat serialization
   */
  getState(): number {
    return this.state;
  }

  /**
   * Set state directly (for resuming mid-combat)
   */
  setState(state: number): void {
    this.state = state;
  }
}

// Global combat RNG instance - set before each battle phase
let combatRNG: SeededRNG | null = null;

/**
 * Set the combat RNG with a new seed
 * Called at the start of each battle phase
 */
export function setCombatSeed(seed: number): void {
  combatRNG = new SeededRNG(seed);
}

/**
 * Get the current combat RNG instance
 * Returns null if not initialized (single-player uses Math.random)
 */
export function getCombatRNG(): SeededRNG | null {
  return combatRNG;
}

/**
 * Clear the combat RNG (revert to Math.random for single-player)
 */
export function clearCombatRNG(): void {
  combatRNG = null;
}

/**
 * Get a random number using combat RNG if available, otherwise Math.random
 * This allows gradual migration without breaking single-player
 */
export function combatRandom(): number {
  return combatRNG ? combatRNG.random() : Math.random();
}
